import { useQuery } from "@apollo/client";
import sortBy from "lodash-es/sortBy";
import { useState, ReactElement } from "react";
import { useQueryParam, NumberParam, withDefault } from "use-query-params";

import {
  AddStaffEmailMutation,
  AddStaffEmailMutationVariables,
  ListStaffEmailsQuery,
  RemoveStaffEmailMutation,
  RemoveStaffEmailMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  ADD_STAFF_EMAIL,
  LIST_STAFF_EMAILS,
  REMOVE_STAFF_EMAIL,
} from "~frontend/graphql/staffEmails";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import useMutation from "~frontend/util/useMutation";

import AddStaffEmailDialog from "./AddStaffEmailDialog";
import StaffEmailTable from "./StaffEmailTable";

export default function StaffEmails(): ReactElement {
  useBreadcrumbs([
    {
      label: "Staff Members",
      path: "/staff/staff-emails",
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const { loading, error, data, refetch } =
    useQuery<ListStaffEmailsQuery>(LIST_STAFF_EMAILS);

  const [addStaffEmailDialogOpen, toggleAddStaffEmailDialog] = useState(false);

  const [addStaffEmail] = useMutation<
    AddStaffEmailMutation,
    AddStaffEmailMutationVariables
  >(ADD_STAFF_EMAIL);

  const [removeStaffEmail] = useMutation<
    RemoveStaffEmailMutation,
    RemoveStaffEmailMutationVariables
  >(REMOVE_STAFF_EMAIL, {
    onErrorSnackbarMessage: "Failed to remove staff member",
  });

  const rows = sortBy(data?.listStaffEmails ?? [], "email").slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddStaffEmail(values: AddStaffEmailMutationVariables) {
    const { errors } = await addStaffEmail({
      variables: values,
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      toggleAddStaffEmailDialog(false);
    }
  }

  async function handleRemoveStaffEmail(email: string) {
    await removeStaffEmail({
      variables: { input: { email } },
    });
    await refetch();
  }

  return (
    <PageContainer maxWidth="lg">
      <FlexBox justifyContent="space-between" alignItems="center">
        <PageHeader>Staff Members</PageHeader>
        <TableControls
          addButtons={[
            {
              label: "Add Staff Member",
              onClick: () => toggleAddStaffEmailDialog(true),
              id: "add-staffemail",
            },
          ]}
        />
      </FlexBox>
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <StaffEmailTable
            rows={rows}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={data?.listStaffEmails.length ?? 0}
            handleRemoveStaffEmail={handleRemoveStaffEmail}
          />
        )}
      </TableBox>
      <AddStaffEmailDialog
        open={addStaffEmailDialogOpen}
        onClose={() => toggleAddStaffEmailDialog(false)}
        onSubmit={handleAddStaffEmail}
      />
    </PageContainer>
  );
}
